// ABOUTME: Audit log of TUC configuration changes with filters, server paging, and a details dialog (HCP-112400).
// ABOUTME: The backend limits records to the countries the user may read (VIEW_AUDIT / MANAGE_HCONNECT_CONFIGURATIONS).
import {Content} from '@hconnect/uikit'
import {
  Alert,
  Button,
  DashboardCard,
  Loading,
  PageHeader,
  Pagination,
  Table,
  TextRegular,
  useSortableTable,
} from '@hm/fundamental'
import type {TableColumn} from '@hm/fundamental'
import React, {useState} from 'react'

import {useAllowedHConnectConfigCountries} from '../HConnect/hooks/useAllowedHConnectConfigCountries'
import {AUDIT_LOG_DEFAULT_PAGE_SIZE} from './auditLogTypes'
import type {AuditLogFilters as Filters, AuditLogRecord} from './auditLogTypes'
import {AuditLogDetailsDialog} from './components/AuditLogDetailsDialog'
import {AuditLogFilters} from './components/AuditLogFilters'
import {UserNameCell} from './components/UserNameCell'
import {useAuditLogs} from './hooks/useAuditLogs'

const PAGE_SIZES = [25, 50, 100, 200]

export const AuditLogList = () => {
  const countries = useAllowedHConnectConfigCountries()
  const [filters, setFilters] = useState<Filters>({})
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(AUDIT_LOG_DEFAULT_PAGE_SIZE)
  const [selected, setSelected] = useState<AuditLogRecord | null>(null)
  const {data, isFetching, isError, error} = useAuditLogs(filters, page, pageSize)

  const search = (next: Filters) => {
    setFilters(next)
    setPage(1)
  }

  const columns: TableColumn<AuditLogRecord>[] = [
    {
      id: 'performedAt',
      label: 'Time',
      sortable: false,
      render: (row) => <TextRegular>{new Date(row.performedAt).toLocaleString()}</TextRegular>,
    },
    {
      id: 'countryId',
      label: 'Country',
      sortable: false,
      render: (row) => <TextRegular>{row.countryId ?? '—'}</TextRegular>,
    },
    {
      id: 'businessLine',
      label: 'Business line',
      sortable: false,
      render: (row) => <TextRegular>{row.businessLine ?? '—'}</TextRegular>,
    },
    {
      id: 'entityType',
      label: 'Entity type',
      sortable: false,
      render: (row) => <TextRegular>{row.entityType}</TextRegular>,
    },
    {
      id: 'action',
      label: 'Action',
      sortable: false,
      render: (row) => (
        <TextRegular>{row.reason ? `${row.action} (${row.reason})` : row.action}</TextRegular>
      ),
    },
    {
      id: 'changedFields',
      label: 'Changed fields',
      sortable: false,
      render: (row) => <TextRegular>{row.changedFields.join(', ') || '—'}</TextRegular>,
    },
    {
      id: 'performedBy',
      label: 'Performed by',
      sortable: false,
      render: (row) => <UserNameCell userId={row.performedBy} />,
    },
    {
      id: 'id',
      label: '',
      sortable: false,
      render: (row) => (
        <Button type="button" variant="tertiary" size="small" onClick={() => setSelected(row)}>
          Details
        </Button>
      ),
    },
  ]

  const tableConfig = useSortableTable<AuditLogRecord>(columns, [], {
    prefix: 'audit-log-row',
    postfixFieldName: 'id',
  })

  const renderResult = () => {
    if (isError) {
      return (
        <Alert variant="error">
          {error?.response?.status === 403
            ? 'You are not allowed to view the audit log.'
            : 'Failed to load the audit log.'}
        </Alert>
      )
    }
    if (isFetching && !data) return <Loading size="large" />
    if (!data?.items.length) return <TextRegular>No records found.</TextRegular>

    return (
      <>
        <Table<AuditLogRecord>
          config={tableConfig}
          data={data.items}
          getRowKey={(row) => row.id}
          data-cy="audit-log-table"
        />
        <div className="mt-6">
          <Pagination
            // Remount on new filters so the pagination shows page 1 again.
            key={JSON.stringify(filters)}
            label="Records per page"
            totalItems={data.totalCount}
            availablePageSizes={PAGE_SIZES}
            defaultPage={page - 1}
            defaultPageSize={pageSize}
            onChange={(pageIndex, nextPageSize) => {
              setPage(pageIndex + 1)
              setPageSize(nextPageSize)
            }}
            data-cy="audit-log-pagination"
          />
        </div>
      </>
    )
  }

  return (
    <Content>
      <div className="[&_*]:!text-white">
        <PageHeader data-cy="page-audit-log" className="pl-0 pr-0 md:pl-0 md:pr-0">
          Audit log
        </PageHeader>
      </div>
      <DashboardCard>
        <div className="mb-6">
          <AuditLogFilters countries={countries} onSearch={search} />
        </div>
        {renderResult()}
      </DashboardCard>
      {selected && <AuditLogDetailsDialog record={selected} onClose={() => setSelected(null)} />}
    </Content>
  )
}
